import Plugin from '../core/Plugin';
import * as SelectionUtils from '../SelectionUtils';

/**
 * ListPlugin - Handles bullet and numbered lists
 * Simplified version using native HTML list features
 */
class ListPlugin extends Plugin {
  constructor(editor, options = {}) {
    super(editor, options);
    this.name = 'List';
    // Store bound handlers for cleanup
    this.boundKeydownHandler = null;
    this.boundInputHandler = null;
  }

  init() {
    super.init();
    // Bind handlers once for later removal
    this.boundKeydownHandler = this.keydownHandler.bind(this);
    this.boundInputHandler = this.inputHandler.bind(this);
  }

  /**
   * Called after the editor is fully initialized
   * This is a lifecycle hook invoked by the editor's notifyPluginsEditorReady() method
   * after the richEditor DOM element has been created and is ready for event listeners
   */
  afterEditorInit() {
    // Add event listeners to the richEditor DOM element
    if (this.editor.richEditor) {
      // add a key event listener for the tab key to handle list indentation
      this.editor.richEditor.addEventListener('keydown', this.boundKeydownHandler);
      // add an input event listener to handle auto-conversion of "* " to list
      this.editor.richEditor.addEventListener('input', this.boundInputHandler);
    }
  }

  destroy() {
    // Remove event listeners to prevent memory leaks
    // Check and remove each handler independently to ensure cleanup even in partial initialization scenarios
    if (this.editor.richEditor) {
      if (this.boundKeydownHandler) {
        this.editor.richEditor.removeEventListener('keydown', this.boundKeydownHandler);
      }
      if (this.boundInputHandler) {
        this.editor.richEditor.removeEventListener('input', this.boundInputHandler);
      }
    }
    super.destroy();
  }

  /**
   * Helper method to check if text exactly matches the list trigger pattern
   * The pattern is: asterisk (*) followed by a single space
   * Checks for both regular space (Unicode U+0020, character code 32) 
   * and non-breaking space (Unicode U+00A0, character code 160)
   * @param {string} text - The text to check for exact match
   * @returns {boolean} True if text is exactly '* ' or '*\u00A0'
   */
  isListTriggerPattern(text) {
    return text === '* ' || text === '*\u00A0';
  }

  inputHandler(event) {
    // Handle auto-conversion of "* " to unordered list
    const selection = window.getSelection();

    // Try to find the current paragraph from selection first for better performance
    let paragraph = null;

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      let currentElement = range.commonAncestorContainer;

      // Find closest element node
      while (currentElement && currentElement.nodeType !== Node.ELEMENT_NODE) {
        currentElement = currentElement.parentNode;
      }

      // Check if we're in a paragraph
      while (currentElement && currentElement.tagName !== 'P') {
        if (currentElement === this.editor.richEditor) {
          break;
        }
        currentElement = currentElement.parentNode;
      }

      if (currentElement && currentElement.tagName === 'P') {
        paragraph = currentElement;
      }
    }

    // Fallback: scan all paragraphs if we couldn't find one from selection
    if (!paragraph) {
      const targetElement = event.target || this.editor.richEditor;
      const paragraphs = targetElement.querySelectorAll('p');

      for (const p of paragraphs) {
        if (this.isListTriggerPattern(p.textContent)) {
          paragraph = p;
          break;
        }
      }
    }

    // Check if the paragraph exactly matches the list trigger pattern
    // Browsers often convert trailing spaces to &nbsp; (Unicode U+00A0) in contenteditable
    if (paragraph && this.isListTriggerPattern(paragraph.textContent)) {
      // Convert to unordered list
      const ul = document.createElement('ul');
      const li = document.createElement('li');
      li.appendChild(document.createElement('br'));
      ul.appendChild(li);

      // Replace the paragraph with the list
      paragraph.parentNode.replaceChild(ul, paragraph);

      // Set cursor inside the list item if selection exists
      if (selection && selection.rangeCount > 0) {
        try {
          const newRange = document.createRange();
          newRange.setStart(li, 0);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
        } catch (error) {
          // If cursor positioning fails, just continue without error
          // The list will still be created successfully
        }
      }
    }
  }

  keydownHandler(event) {
    if (event.key === 'Tab') {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return;
      }

      const range = selection.getRangeAt(0);
      let currentElement = range.commonAncestorContainer;

      // Find closest element node
      while (currentElement && currentElement.nodeType !== Node.ELEMENT_NODE) {
        currentElement = currentElement.parentNode;
      }

      // Check if we're inside a list item
      let listItem = currentElement;
      while (listItem && listItem.tagName !== 'LI') {
        if (listItem.classList && listItem.classList.contains('quillkit-preview')) {
          listItem = null;
          break;
        }
        listItem = listItem.parentNode;
      }

      if (listItem && listItem.tagName === 'LI') {
        event.preventDefault();
        
        if (event.shiftKey) {
          SelectionUtils.outdentListItem(listItem);
        } else {
          // Indent
          SelectionUtils.indentListItem(listItem);
        }

        // Restore cursor to the list item after DOM manipulation
        // The listItem reference is still valid even after being moved in the DOM
        try {
          if (listItem.isConnected) {
            const newRange = document.createRange();
            newRange.selectNodeContents(listItem);
            newRange.collapse(false); // Collapse to end
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        } catch (error) {
          // Log cursor positioning failures for debugging
          console.warn('Failed to restore cursor position after list indent/outdent:', error);
        }

        // Sync editor state
        if (this.editor) {
          if (this.editor.syncToTextarea) {
            this.editor.syncToTextarea();
          }
          if (this.editor.saveToHistory) {
            this.editor.saveToHistory();
          }
        }
      }
    }
  }

  getToolbarButtons() {
    return [
      {
        name: 'unordered-list-split',
        splitButton: {
          main: {
            command: 'insertUnorderedList',
            label: '<i class="fa-solid fa-list-ul"></i>',
            title: 'Bullet List',
          },
          dropdown: {
            command: 'bullet-style',
            title: 'Select Bullet Style',
            options: [
              { value: 'disc', label: '● Disc' },
              { value: 'circle', label: '○ Circle' },
              { value: 'square', label: '■ Square' },
              { value: 'none', label: 'None' },
            ],
          },
        },
      },
      {
        name: 'ordered-list-split',
        splitButton: {
          main: {
            command: 'insertOrderedList',
            label: '<i class="fa-solid fa-list-ol"></i>',
            title: 'Numbered List',
          },
          dropdown: {
            command: 'numbering-style',
            title: 'Select Numbering Style',
            options: [
              { value: 'decimal', label: '1, 2, 3' },
              { value: 'decimal-paren', label: '1), 2), 3)' },
              { value: 'lower-alpha', label: 'a, b, c' },
              { value: 'upper-alpha', label: 'A, B, C' },
              { value: 'lower-roman', label: 'i, ii, iii' },
              { value: 'upper-roman', label: 'I, II, III' },
              { value: 'none', label: 'None' },
            ],
          },
        },
      },
      {
        name: 'lists',
        buttons: [
          {
            command: 'setListStart',
            label: '<i class="fa-solid fa-list-ol"></i>',
            title: 'Set List Start Number',
          },
          {
            command: 'setListItemValue',
            label: '<i class="fa-solid fa-hashtag"></i>',
            title: 'Set List Item Number',
          },
        ],
      },
    ];
  }

  executeCommand(command) {
    switch (command) {
      case 'insertUnorderedList':
        SelectionUtils.toggleList('ul');
        break;
      case 'insertOrderedList':
        SelectionUtils.toggleList('ol');
        break;
      case 'setListStart':
        this.setListStart();
        break;
      case 'setListItemValue':
        this.setListItemValue();
        break;
      default:
        // Handle bullet-style-* and numbering-style-* commands
        if (command.startsWith('bullet-style-')) {
          const style = command.replace('bullet-style-', '');
          this.handleBulletStyle(style);
        } else if (command.startsWith('numbering-style-')) {
          const style = command.replace('numbering-style-', '');
          this.handleNumberingStyle(style);
        }
        break;
    }
  }

  /**
   * Handle bullet style changes
   */
  handleBulletStyle(style) {
    const current = SelectionUtils.getCurrentListStyle && SelectionUtils.getCurrentListStyle('ul');

    if (style === 'none') {
      // Always remove list if 'none' selected
      SelectionUtils.toggleList('ul', true);
    } else if (current === style) {
      // If re-selecting the same style, remove the list
      SelectionUtils.toggleList('ul', true);
    } else {
      // If selecting a different style, apply the new style
      if (!SelectionUtils.isListActive || !SelectionUtils.isListActive('ul')) {
        SelectionUtils.toggleList('ul');
      }
      SelectionUtils.applyListStyle(style);
    }
  }

  /**
   * Handle numbering style changes
   */
  handleNumberingStyle(style) {
    const current = SelectionUtils.getCurrentListStyle && SelectionUtils.getCurrentListStyle('ol');

    if (style === 'none') {
      // Always remove list if 'none' selected
      SelectionUtils.toggleList('ol', true);
    } else if (current === style) {
      // If re-selecting the same style, remove the list
      SelectionUtils.toggleList('ol', true);
    } else {
      // If selecting a different style, apply the new style
      if (!SelectionUtils.isListActive || !SelectionUtils.isListActive('ol')) {
        SelectionUtils.toggleList('ol');
      }
      SelectionUtils.applyListStyle(style);
    }
  }

  /**
   * Set the start number for an ordered list
   * This sets the 'start' attribute on the <ol> element
   */
  setListStart() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    let currentElement = range.commonAncestorContainer;

    // Find closest element node
    while (currentElement && currentElement.nodeType !== Node.ELEMENT_NODE) {
      currentElement = currentElement.parentNode;
    }

    // Find the ordered list element
    let listElement = currentElement;
    while (listElement && listElement.tagName !== 'OL') {
      if (listElement.classList && listElement.classList.contains('quillkit-preview')) {
        listElement = null;
        break;
      }
      listElement = listElement.parentNode;
    }

    if (!listElement || listElement.tagName !== 'OL') {
      alert('Please place your cursor inside a numbered list first.');
      return;
    }

    // Get current start value if any
    const currentStart = listElement.getAttribute('start') || '1';
    const promptMessage = 'Enter the starting number for this list (default is 1):';

    const newStart = prompt(promptMessage, currentStart);

    // User cancelled
    if (newStart === null) {
      return;
    }

    // Validate that it's a positive integer
    const numValue = parseInt(newStart, 10);
    if (isNaN(numValue) || numValue < 1) {
      alert('Please enter a valid positive number (1 or greater).');
      return;
    }

    // Set the start attribute
    if (numValue === 1) {
      // Remove start attribute if setting to default value
      listElement.removeAttribute('start');
    } else {
      listElement.setAttribute('start', numValue.toString());
    }

    // Trigger editor sync
    if (this.editor) {
      this.editor.syncToTextarea();
      this.editor.saveToHistory();
    }
  }

  /**
   * Set a custom value for a specific list item
   * This sets the 'value' attribute on the <li> element
   */
  setListItemValue() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    let currentElement = range.commonAncestorContainer;

    // Find closest element node
    while (currentElement && currentElement.nodeType !== Node.ELEMENT_NODE) {
      currentElement = currentElement.parentNode;
    }

    // Find the list item element
    let listItem = currentElement;
    while (listItem && listItem.tagName !== 'LI') {
      if (listItem.classList && listItem.classList.contains('quillkit-preview')) {
        listItem = null;
        break;
      }
      listItem = listItem.parentNode;
    }

    if (!listItem || listItem.tagName !== 'LI') {
      alert('Please place your cursor inside a list item first.');
      return;
    }

    // Check if this is an ordered list item
    const parentList = listItem.parentNode;
    if (!parentList || parentList.tagName !== 'OL') {
      alert('This feature only works with numbered (ordered) lists.');
      return;
    }

    // Get current value if any
    const currentValue = listItem.getAttribute('value');
    const promptMessage = currentValue
      ? `Current number: ${currentValue}\n\nEnter a new number for this list item (or leave empty to use automatic numbering):`
      : 'Enter a number for this list item (or leave empty to use automatic numbering):';

    const newValue = prompt(promptMessage, currentValue || '');

    // User cancelled
    if (newValue === null) {
      return;
    }

    // Remove value attribute if empty
    if (newValue.trim() === '') {
      listItem.removeAttribute('value');
    } else {
      // Validate that it's a positive integer
      const numValue = parseInt(newValue, 10);
      if (isNaN(numValue) || numValue < 1) {
        alert('Please enter a valid positive number (1 or greater).');
        return;
      }
      listItem.setAttribute('value', numValue.toString());
    }

    // Trigger editor sync
    if (this.editor) {
      this.editor.syncToTextarea();
      this.editor.saveToHistory();
    }
  }

  getMetadata() {
    return {
      name: 'Lists',
      version: '2.1.0',
      description: 'Provides bullet and numbered list functionality with native HTML features',
      author: 'AtlaraFirm',
    };
  }
}

export default ListPlugin;
